import { Theme } from '@mui/material/styles';

/**
 * Get dynamic styles based on theme
 */
export const getDynamicStyles = (theme: Theme) => ({
  container: {
    padding: 3,
    marginTop: 2
  },
  contentWrapper: {
    width: '100%',
    maxWidth: '98%',
    mx: 'auto',
    overflowX: 'visible', 
    overflowY: 'visible'
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.background.paper,
    padding: '12px 16px',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  tableContainer: {
    mt: 3,
    width: '100%',
    boxShadow: theme.shadows[2],
    borderRadius: 1
  },
  table: {
    tableLayout: 'fixed',
    width: '100%'
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    padding: '8px 16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  warningRow: {
    backgroundColor: '#fffec8',
  },
  disabledRow: {
    backgroundColor: theme.palette.action.disabledBackground,
    opacity: 0.7
  },
  formControl: {
    minWidth: 200,
    mb: 2
  },
  actionButtonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    mb: 2
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    mb: 2
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
    width: '100%'
  },
  noResultsMessage: {
    p: 4, 
    width: '100%', 
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    border: '1px dashed',
    borderColor: theme.palette.grey[300]
  },
  filterContainer: {
    p: 3, 
    bgcolor: 'background.default', 
    borderRadius: 1, 
    border: '1px solid', 
    borderColor: 'divider',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  }
});

/**
 * Table cell style helpers
 */
export const getTableCellStyle = (column: { minWidth: number; maxWidth: number }) => ({
  width: column.minWidth,
  maxWidth: column.maxWidth,
  padding: '8px 16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

/**
 * Row style based on item properties
 */
export const getRowStyle = (theme: Theme, item: { canEdit?: boolean }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  ...(item.canEdit === false ? { backgroundColor: '#fffec8' } : {})
});

/**
 * Performance metrics display styles
 */
export const getPerformanceMetricsStyles = (theme: Theme) => ({
  container: { 
    p: 2, 
    ml: 2,
    mr: 2, 
    maxHeight: '200px', 
    overflow: 'auto' 
  },
  title: {
    variant: 'h6' as const
  },
  content: {
    component: 'pre' as const,
    sx: { fontSize: '0.8rem' }
  }
});

/**
 * Button styles
 */
export const getButtonStyles = () => ({
  createButton: { 
    whiteSpace: 'nowrap', 
    padding: '6px 26px !important',
    textTransform: 'none',
    boxShadow: 1
  }
});

/**
 * Dialog styles
 */
export const getDialogStyles = (theme: Theme) => ({
  paper: {
    borderRadius: 1,
    boxShadow: theme.shadows[10]
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    mb: 2
  },
  actions: { 
    p: 2, 
    borderTop: `1px solid ${theme.palette.divider}` 
  },
  formContainer: {
    display: 'flex', 
    flexDirection: { xs: 'column', md: 'row' } as const, 
    gap: 2
  }
}); 